import { useCallback } from "react";
import {
  condition,
  conditionItem,
  updateState,
} from "./Actions/actionInterface";
import getData from "./getData";
import { getAllCookies } from "./clientStorageActions";
import { globalRegex } from "@/utils/constants";
import { useAppState } from "@/context/AppStateContext";

const useConditions = (pageState: any) => {
  const {
    state: { pageStore, layoutStore },
  } = useAppState();

  const checkCondition = (data_1: any, data_2: any, condition: string) => {
    const operators: {
      [key: string]: Function;
    } = {
      "<": (a: any, b: any) => a < b,
      "<=": (a: any, b: any) => a <= b,
      ">": (a: any, b: any) => a > b,
      ">=": (a: any, b: any) => a >= b,
      "==": (a: any, b: any) => a == b,
      "===": (a: any, b: any) => a === b,
      "!==": (a: any, b: any) => a !== b,
      "!=": (a: any, b: any) => a != b,
    };

    return operators[condition] ? operators[condition](data_1, data_2) : false;
  };

  const checkData = (
    data1: updateState | string,
    data2: any,
    typeOfData: string
  ) => {
    switch (typeOfData) {
      case "Array":
        return data1 && Array.isArray(data1) && data1?.length ? true : false;
      case "InArray":
        return data1 && Array.isArray(data1) && data1.includes(data2)
          ? true
          : false;
      case "NotInArray":
        return data1
          ? Array.isArray(data1)
            ? !data1.includes(data2)
            : true
          : true;
      case "Object":
        return data1 && Object.keys(data1)?.length ? true : false;
      case "string":
        return data1 &&
          data1 !== "" &&
          (typeof data1 === "string" || typeof data1 === "number")
          ? true
          : false;
      case "hasValue":
        return data1 !== null && data1 !== undefined;
      case "undefined":
        return data1 === undefined;
      case "null":
        return data1 === null;
      case "key-in":
        return Object.hasOwn(data2 ?? {}, String(data1));
      case "key-not-in":
        return !Object.hasOwn(data2 ?? {}, String(data1));
      default:
        false;
    }
  };

  const getData1and2 = useCallback(
    (
      item: conditionItem,
      globalState?: any,
      fullGlobalState?: any,
      collectionStateOrPayload?: any
    ) => {
      const reduxStores = { page: pageStore, layout: layoutStore };

      const resolveSource = (sourceType?: string): any => {
        switch (sourceType) {
          case "GLOBAL":
            return fullGlobalState;
          case "DEFAULT":
            return collectionStateOrPayload;
          case "REDUX":
            return reduxStores;
          case "LOCALSTORAGE":
            return {};
          case "COOKIES":
            return getAllCookies();
          default:
            return globalState;
        }
      };

      const extractData = (
        from: any,
        getType: string | undefined,
        source: any
      ): any => {
        const extracted =
          typeof source === "object"
            ? getData(
                from,
                source,
                globalState,
                fullGlobalState,
                reduxStores,
                collectionStateOrPayload
              )
            : source;
        return getType === "length" ? extracted?.length : extracted;
      };

      // Init values
      let data1Value: any = item?.data1From;
      let data2Value: any = item?.data2From === null ? null : item?.data2From;
      let data1Source: any = globalState;
      let data2Source: any = globalState;

      // Resolve data1 if it's an object
      if (typeof item?.data1From === "object") {
        data1Source = resolveSource(item?.data1From?.source);
        data1Value = extractData(
          item?.data1From,
          item?.data1GetType,
          data1Source
        );
      }

      // Resolve data2 if it's an object
      if (typeof item?.data2From === "object") {
        data2Source = resolveSource(item?.data2From?.source);
        data2Value = extractData(
          item?.data2From,
          item?.data2GetType,
          data2Source
        );
      }

      return { data1Value, data2Value, data1Source, data2Source };
    },
    [pageState, layoutStore, pageStore]
  );

  const verifyConditions = useCallback(
    (
      conditions: condition[],
      payload: any,
      globalState?: any,
      fullGlobalState?: any
    ) => {
      if (!conditions?.length) return true;

      const evaluateItem = (item: conditionItem) => {
        const { data1Value, data2Value, data1Source, data2Source } =
          getData1and2(item, globalState, fullGlobalState, payload);

        // Regex case
        if (item?.comparisonType === "regex" && item?.regex) {
          const testValue =
            typeof item?.data1From === "object"
              ? getData(
                  item?.data1From,
                  data1Source,
                  globalState,
                  fullGlobalState,
                  { page: pageStore, layout: layoutStore },
                  payload
                )
              : item?.data1From ?? payload;

          const regEx = globalRegex[item?.regex] || new RegExp(item?.regex);
          return regEx.test(testValue);
        }

        // CHECK_DATA case
        if (item?.comparisonType === "CHECK_DATA") {
          const val1 =
            typeof item?.data1From === "object"
              ? getData(
                  item?.data1From,
                  data1Source,
                  globalState,
                  fullGlobalState,
                  { page: pageStore, layout: layoutStore },
                  payload
                )
              : item?.data1From;

          const val2 =
            typeof item?.data2From === "object"
              ? getData(
                  item?.data2From,
                  data2Source,
                  globalState,
                  fullGlobalState,
                  { page: pageStore, layout: layoutStore },
                  payload
                )
              : item?.data2From;

          return checkData(val1, val2, item?.typeOfData ?? "");
        }

        // Fallback to basic condition check
        return checkCondition(data1Value, data2Value, item?.comparisonType);
      };

      return conditions.every((condition) => {
        const items = condition?.items ?? [];
        return condition.conditionType === "OR"
          ? items.some(evaluateItem)
          : items.every(evaluateItem);
      });
    },
    [pageState, layoutStore, pageStore]
  );

  return verifyConditions;
};

export default useConditions;
